import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';

import {
  PackageRowActionItem,
  PackageRowActionsComponent,
} from '../package-row-actions/package-row-actions.component';
import { DataTableCellDefDirective } from './data-table-cell-def.directive';
import {
  DataTableColumn,
  DataTableFilter,
  DataTableFilterChange,
  DataTableRowAction,
  DataTableRowActionEvent,
  DataTableSortState,
} from './data-table.types';

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatMenuModule,
    MatCheckboxModule,
    PackageRowActionsComponent,
  ],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DataTableComponent<T> implements AfterContentInit, OnChanges {
  @Input({ required: true }) columns: DataTableColumn<T>[] = [];
  @Input({ required: true }) data: T[] = [];
  @Input() filters: DataTableFilter[] = [];
  @Input() rowActions: DataTableRowAction<T>[] = [];
  @Input() sort: DataTableSortState | null = null;
  @Input() testId = 'data-table';
  @Input() minTableWidth = '1200px';
  @Input() rowClickable = false;
  @Input() rowId: (row: T) => string = row => String((row as { id?: string }).id ?? '');

  @Output() readonly sortChange = new EventEmitter<DataTableSortState>();
  @Output() readonly rowClick = new EventEmitter<T>();
  @Output() readonly rowAction = new EventEmitter<DataTableRowActionEvent<T>>();
  @Output() readonly filterChange = new EventEmitter<DataTableFilterChange>();

  @ContentChildren(DataTableCellDefDirective) cellTemplates?: QueryList<
    DataTableCellDefDirective<T>
  >;

  readonly dataSource = new MatTableDataSource<T>();

  private cellTemplateMap = new Map<string, DataTableCellDefDirective<T>['templateRef']>();
  private pendingFilterValues = new Map<string, string[]>();
  private openActionRowId: string | null = null;
  activeFilterTrigger: MatMenuTrigger | null = null;
  actionRow: T | null = null;
  /** Field key for the column filter menu currently open (drives the shared menu panel). */
  activeFilterField: string | null = null;

  ngAfterContentInit(): void {
    this.dataSource.data = this.data;
    this.bindCellTemplates();
    this.cellTemplates?.changes.subscribe(() => this.bindCellTemplates());
  }

  get displayedColumns(): string[] {
    const keys = this.columns.map(column => column.key);
    return this.rowActions.length > 0 ? [...keys, 'actions'] : keys;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.dataSource.data = this.data;
    }
    if (changes['filters']) {
      this.syncPendingFilters();
    }
  }

  getCellTemplate(columnKey: string) {
    return this.cellTemplateMap.get(columnKey) ?? null;
  }

  getColumnValue(row: T, column: DataTableColumn<T>): unknown {
    if (column.valueAccessor) {
      return column.valueAccessor(row);
    }
    return this.readField(row, column.key);
  }

  getFilterForColumn(column: DataTableColumn<T>): DataTableFilter | undefined {
    if (!column.filterField) {
      return undefined;
    }
    return this.filters.find(filter => filter.field === column.filterField);
  }

  /** Filter config for the shared column filter menu (set when a header filter opens). */
  get activeFilter(): DataTableFilter | undefined {
    if (!this.activeFilterField) {
      return undefined;
    }
    return this.filters.find(filter => filter.field === this.activeFilterField);
  }

  hasActiveFilterValues(field: string): boolean {
    const filter = this.filters.find(item => item.field === field);
    return (filter?.activeValues?.length ?? 0) > 0;
  }

  getPendingFilterValues(field: string): string[] {
    return this.pendingFilterValues.get(field) ?? [];
  }

  isFilterOptionSelected(field: string, value: string): boolean {
    return this.getPendingFilterValues(field).includes(value);
  }

  onFilterOptionToggle(field: string, value: string, checked: boolean): void {
    const current = this.getPendingFilterValues(field);
    const next = checked
      ? current.includes(value)
        ? current
        : [...current, value]
      : current.filter(item => item !== value);
    this.pendingFilterValues.set(field, next);
  }

  onColumnFilterMenuOpened(field: string, trigger: MatMenuTrigger): void {
    this.activeFilterField = field;
    this.activeFilterTrigger = trigger;
    const filter = this.filters.find(item => item.field === field);
    this.pendingFilterValues.set(field, [...(filter?.activeValues ?? [])]);
  }

  onColumnFilterMenuClosed(): void {
    const field = this.activeFilterField;
    if (field) {
      const filter = this.filters.find(item => item.field === field);
      this.pendingFilterValues.set(field, [...(filter?.activeValues ?? [])]);
    }
    this.activeFilterField = null;
    this.activeFilterTrigger = null;
  }

  applyActiveColumnFilter(): void {
    if (!this.activeFilterField) {
      return;
    }
    this.applyColumnFilter(this.activeFilterField, this.activeFilterTrigger ?? undefined);
  }

  clearActiveColumnFilter(): void {
    if (!this.activeFilterField) {
      return;
    }
    this.clearColumnFilter(this.activeFilterField, this.activeFilterTrigger ?? undefined);
  }

  applyColumnFilter(field: string, trigger?: MatMenuTrigger): void {
    this.filterChange.emit({
      field,
      values: [...this.getPendingFilterValues(field)],
    });
    trigger?.closeMenu();
  }

  clearColumnFilter(field: string, trigger?: MatMenuTrigger): void {
    this.pendingFilterValues.set(field, []);
    this.filterChange.emit({ field, values: [] });
    trigger?.closeMenu();
  }

  onSortHeaderClick(column: DataTableColumn<T>): void {
    if (!column.sortable) {
      return;
    }
    const current = this.sort;
    const nextDirection: 'asc' | 'desc' =
      current?.column === column.key && current.direction === 'desc' ? 'asc' : 'desc';
    this.sortChange.emit({ column: column.key, direction: nextDirection });
  }

  getSortIcon(column: DataTableColumn<T>): string {
    if (!column.sortable) {
      return '';
    }
    if (this.sort?.column !== column.key) {
      return 'assets/images/package/tabler_arrows-sort.svg';
    }
    return this.sort.direction === 'asc'
      ? '/assets/images/time_sort_ascending.png'
      : '/assets/images/time_sort_descending.png';
  }

  onRowClicked(row: T): void {
    if (this.openActionRowId) {
      this.openActionRowId = null;
      this.actionRow = null;
      return;
    }
    this.rowClick.emit(row);
  }

  onActionMenuOpened(row: T): void {
    this.actionRow = row;
    this.openActionRowId = this.rowId(row);
  }

  onRowActionSelected(actionId: string): void {
    if (!this.actionRow) {
      return;
    }
    this.rowAction.emit({ action: actionId, row: this.actionRow });
    this.openActionRowId = null;
    this.actionRow = null;
  }

  rowActionItems(row: T): PackageRowActionItem[] {
    return this.rowActions
      .filter(action => !action.hidden?.(row) && !action.disabled?.(row))
      .map(action => ({
        id: action.key,
        label: action.label,
        icon: action.icon ?? '',
        iconSize: action.iconSize,
        danger: action.danger,
        borderTop: action.borderTop,
      }));
  }

  private bindCellTemplates(): void {
    this.cellTemplateMap.clear();
    this.cellTemplates?.forEach(def => {
      this.cellTemplateMap.set(def.columnKey, def.templateRef);
    });
  }

  private syncPendingFilters(): void {
    this.filters.forEach(filter => {
      // Parent screens often pass a freshly built `filters` array each CD cycle
      // (e.g. from a getter). Do not clobber checkbox selections while the menu is open.
      if (this.isColumnFilterMenuOpen(filter.field)) {
        return;
      }

      const next = [...(filter.activeValues ?? [])];
      const current = this.pendingFilterValues.get(filter.field);
      if (current && this.areStringArraysEqual(current, next)) {
        return;
      }

      this.pendingFilterValues.set(filter.field, next);
    });
  }

  /** True while a column filter menu is being edited (from trigger click until close). */
  private isColumnFilterMenuOpen(field: string): boolean {
    return this.activeFilterField === field;
  }

  private areStringArraysEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((value, index) => value === right[index]);
  }

  private readField(row: T, key: string): unknown {
    return key.split('.').reduce<unknown>((value, segment) => {
      if (value === null || value === undefined) {
        return undefined;
      }
      return (value as Record<string, unknown>)[segment];
    }, row as unknown);
  }
}
